// Card 40, the legend (R548, ui-map-interface.md §17.3.40).
//
// Chart 5011 is the Admiralty's book of symbols, and every chart carries
// the ones it uses in a legend. The KEYS panel already teaches the
// keyboard, so it takes one more group — "Chart symbols" — with one row per
// mark the picked pieces draw.
//
// **THE LEGEND CANNOT DRIFT FROM THE CHART.** Every swatch is drawn by the
// SAME function of `render/chart-glyphs.ts` that the map piece calls,
// through the Canvas2D adapter of `render/canvas-sink.ts`, and with the
// SAME figures out of `chart.toml`. The two sides share one table of
// expected call counts (`testkit/legend-counts.ts`) which both tests
// assert against, so a glyph that changes shape fails in the legend and on
// the map at once.
//
// **NOTHING HERE IS ON THE MAP,** so the legend is not in the furniture
// registry and mounts nothing in the `#furniture` host. It reads its own
// `CHART.pieces.legend` gate — the form factor and the reading — and the
// overlay calls it when the panel opens and when the reading changes.
//
// **A ROW OF V2 STANDS UNDER ITS FLAG** (R2616). The harbour limits of
// card 28 draw only while the world latched `modifiers` (R2585), so their
// row names the flag, and the panel hands the latched set in.
//
// **A ROW STANDS WHILE ITS PIECE DRAWS** (R2327). Each row names the piece whose
// mark it teaches, and the row stands only while that piece is on in the
// reading, by the Chart theme of the look first and the pack second, the
// gate of `fast/chart/furniture.ts`. The hazards piece is off in both
// readings of the pack (R561), so its three rows stand only under a theme
// that turns it on.

import { chart as S } from '../../gen/strings';
import { CHART, type ChartPieceId } from '../../gen/presentation';
import { RESOURCES } from '../../gen/balance';
import { CanvasSink, type Ctx2D } from '../../render/canvas-sink';
import type { CircleSink, SegmentSink } from '../../fast/layers/batches';
import {
  crossDots, diamond, flare, flareRays, star, swords,
} from '../../render/chart-glyphs';
import { chartFont } from '../../fast/gfx/atlas';
import { CHART_INK, dashedRing, faceOf } from '../../fast/chart/pieces/shared';
import { stepPx } from '../../fast/chart/steps';
import type { ChartReading, ChartSetting } from '../../fast/chart/context';
import type { ChartPieceOverlay } from '../../fast/chart/furniture';
import { hex } from '../../world';
import type { FeatureFlag } from '../../gen/featureflags';

/** The letter a row may write instead of a shape (card 14). */
export interface LegendText {
  s: string;
  color: number;
  alpha: number;
}

/**
 * What a swatch paints into.
 *
 * It is the two sink interfaces and NOT `CanvasSink`, so the same row can
 * be painted into a recorder in a test and read back call by call.
 * `CanvasSink` wears both.
 */
export type SwatchSink = SegmentSink & CircleSink;

/**
 * A glyph leaf of `render/chart-glyphs.ts` that a swatch calls. Each name is
 * a row of the shared counts table (`testkit/legend-counts.ts`), and the
 * legend test hands these names to that table, so the type check fails on a
 * name the table does not hold. The table is test code, so this module
 * states the names it uses and does not import them.
 */
export type LegendGlyph =
  | 'crossDots' | 'crossDotsWithDots' | 'star' | 'swords' | 'diamond' | 'flare' | 'flareRays';

/** One row of the legend: a swatch, the words, and what the swatch costs. */
export interface LegendRow {
  /** The row's own key, for a test and for the DOM. */
  key: string;
  /** The chart piece whose mark the row teaches. */
  piece: ChartPieceId;
  /** The words beside the swatch. */
  label: string;
  /** The glyph leaves this row's swatch calls, for the shared counts table. */
  glyphs: readonly LegendGlyph[];
  /** Paints the swatch, centred on `(x, y)` of the sink's own canvas. */
  paint(sink: SwatchSink, x: number, y: number): void;
  /** A row that writes a letter rather than a mark. */
  text?: LegendText;
  /** The feature flag the row stands under, as its piece draws only then
   *  (R2616). */
  flag?: FeatureFlag;
}

/** The magenta a contested cell and a light are drawn in. */
const MAGENTA = CHART_INK.magenta;

/** The gold a Beacon's limit line is drawn in: the ink the piece itself reads. */
const GOLD = CHART_INK.gold;

/** The attack red the torpedo run and the battle mark letter in (R548). */
const ATTACK_INK = CHART.pieces.torpedo_run.modern.ink;

/** Half the width of a straight-rule swatch, in the swatch's own px. */
const RULE_HALF = 9;

/** The radius a ring swatch is drawn at, in the swatch's own px. */
const RING_R = 6;

/** A dashed straight rule, in the pattern the piece's own pack states. */
function rule(
  sink: SwatchSink, x: number, y: number, dash: readonly number[],
  color: number, alpha: number, width: number,
): void {
  sink.line(x - RULE_HALF, y, x + RULE_HALF, y, width, color, alpha, 0,
    dash[0] ?? 0, dash[1] ?? 0);
}

/** A dashed ring, in the pattern the piece's own pack states. */
function ring(
  sink: SwatchSink, x: number, y: number, dash: readonly number[],
  color: number, alpha: number, width: number,
): void {
  dashedRing(sink, x, y, RING_R, width, dash, color, alpha);
}

/** Is the piece on in the reading: the Chart theme first, then the pack
 *  (the gate of `fast/chart/furniture.ts`). */
function pieceOn(
  id: ChartPieceId, reading: ChartReading, overlay: ChartPieceOverlay | null = null,
): boolean {
  return overlay?.[id]?.[reading]?.on ?? CHART.pieces[id][reading].on;
}

/**
 * The rows, in the order the KEYS panel lists them: every row whose piece
 * is on in the reading.
 *
 * Ten pieces draw a mark a reader has to learn, and the twelve rows here
 * are those ten: card 26 teaches three marks and the rest teach one each.
 */
export function legendRows(
  reading: ChartReading, overlay: ChartPieceOverlay | null = null,
  has: (flag: FeatureFlag) => boolean = () => false,
): LegendRow[] {
  return everyRow(reading).filter((r) => pieceOn(r.piece, reading, overlay) && (!r.flag || has(r.flag)));
}

/** The thirteen rows, whether their piece is on or not. */
function everyRow(reading: ChartReading): LegendRow[] {
  const hist = reading === 'historical';
  const P = CHART.pieces;
  const ink = CHART_INK.pale;
  return [
    {
      key: 'commodity', piece: 'bottom_nature', label: S.legend_commodity, glyphs: [],
      // Card 14 writes a letter and draws no shape, so the swatch is the
      // letter itself, in the commodity's own colour.
      paint: () => { /* the letter is written by the caller */ },
      text: { s: RESOURCES[0].short, color: RESOURCES[0].color, alpha: 0.9 },
    },
    {
      key: 'rock_awash', piece: 'hazards', label: S.legend_rock_awash,
      glyphs: [hist ? 'crossDots' : 'crossDotsWithDots'],
      paint: (sink, x, y) => crossDots(
        sink, sink, x, y, P.hazards.fig.cross_r, ink,
        P.hazards[reading].alpha, P.hazards[reading].width, !hist,
      ),
    },
    {
      key: 'drying_rock', piece: 'hazards', label: S.legend_drying_rock,
      glyphs: [hist ? 'crossDots' : 'star'],
      paint: (sink, x, y) => (hist
        ? crossDots(sink, sink, x, y, P.hazards.fig.cross_r, ink,
          P.hazards.historical.alpha, P.hazards.historical.width)
        : star(sink, x, y, P.hazards.fig.star_r, ink,
          P.hazards.modern.alpha, P.hazards.modern.width)),
    },
    {
      key: 'danger_line', piece: 'hazards', label: S.legend_danger_line, glyphs: [],
      paint: (sink, x, y) => rule(
        sink, x, y, P.hazards[reading].dash, ink,
        P.hazards.fig.line_alpha, P.hazards[reading].width,
      ),
    },
    {
      key: 'contested', piece: 'restricted', label: S.legend_contested, glyphs: [],
      paint: (sink, x, y) => rule(
        sink, x, y, P.restricted[reading].dash, hist ? ink : MAGENTA,
        P.restricted.fig.alpha, P.restricted.fig.width,
      ),
    },
    {
      key: 'torpedo_run', piece: 'torpedo_run', label: S.legend_torpedo_run, glyphs: [],
      paint: (sink, x, y) => ring(
        sink, x, y, P.torpedo_run[reading].dash, ATTACK_INK,
        P.torpedo_run.fig.ring_alpha, P.torpedo_run.fig.width,
      ),
    },
    {
      key: 'battle_mark', piece: 'battle_marks', label: S.legend_battle_mark, glyphs: ['swords'],
      paint: (sink, x, y) => swords(
        sink, sink, x, y, hist ? ink : ATTACK_INK,
        P.battle_marks.fig.alpha_new, P.battle_marks[reading].width,
      ),
    },
    {
      key: 'spoil_ground', piece: 'spoil_ground', label: S.legend_spoil_ground, glyphs: [],
      paint: (sink, x, y) => ring(
        sink, x, y, P.spoil_ground[reading].dash, ink,
        P.spoil_ground.fig.alpha, P.spoil_ground.fig.width,
      ),
    },
    {
      key: 'tidal_diamond', piece: 'tidal_diamond', label: S.legend_tidal_diamond, glyphs: ['diamond'],
      paint: (sink, x, y) => diamond(
        sink, x, y, P.tidal_diamond.fig.half_w, P.tidal_diamond.fig.half_h,
        MAGENTA, P.tidal_diamond.fig.alpha, P.tidal_diamond.fig.width,
      ),
    },
    {
      key: 'light', piece: 'light_chars', label: S.legend_light, glyphs: [hist ? 'flareRays' : 'flare'],
      paint: (sink, x, y) => (hist
        ? flareRays(sink, x, y, MAGENTA, P.light_chars.fig.flare_alpha,
          P.light_chars.historical.width,
          P.light_chars.historical.fig.ray_r0, P.light_chars.historical.fig.ray_r1)
        : flare(sink, x, y + P.light_chars.fig.flare_h / 2, P.light_chars.fig.flare_h,
          MAGENTA, P.light_chars.fig.flare_alpha, P.light_chars.modern.width)),
    },
    {
      key: 'bank', piece: 'banks', label: S.legend_bank, glyphs: [],
      paint: (sink, x, y) => ring(
        sink, x, y, P.banks[reading].dash, ink,
        P.banks.fig.alpha, P.banks.fig.width,
      ),
    },
    {
      key: 'limit_line', piece: 'limit_line', label: S.legend_limit_line, glyphs: [],
      paint: (sink, x, y) => ring(
        sink, x, y, P.limit_line[reading].dash, GOLD,
        P.limit_line.fig.alpha, P.limit_line.fig.width,
      ),
    },
    {
      // Card 28 draws in the faction ink of each hold; the row teaches the
      // dash in the pale ink, because it names no faction. The line is the
      // dashed hex line of card 30, so the row draws a rule as the row of
      // card 30 does (R2727).
      key: 'harbour_limits', piece: 'harbour_limits', label: S.legend_harbour_limits, glyphs: [],
      flag: 'modifiers',
      paint: (sink, x, y) => rule(
        sink, x, y, P.harbour_limits[reading].dash, ink,
        P.harbour_limits.fig.alpha, P.harbour_limits.fig.width,
      ),
    },
  ];
}

/** Does the legend draw at all, at this reading and this form factor? */
export function legendShows(setting: ChartSetting, sheet: boolean): boolean {
  if (setting === 'off') return false;
  const t = CHART.pieces.legend;
  if (!(sheet ? t.mobile : t.desktop)) return false;
  return t[setting].on;
}

/**
 * The KEYS panel's own "Chart symbols" group, or null when the gate is shut.
 *
 * The group wears the panel's own classes, so it reads exactly like the key
 * rows over it and the stylesheet carries nothing of its own.
 */
export function legendGroup(
  setting: ChartSetting, sheet: boolean, overlay: ChartPieceOverlay | null = null,
  has: (flag: FeatureFlag) => boolean = () => false,
): HTMLElement | null {
  if (!legendShows(setting, sheet)) return null;
  const reading = setting as ChartReading;
  const t = CHART.pieces.legend;
  const r = t[reading];
  const wrap = document.createElement('div');
  wrap.className = 'chart-legend';

  const head = document.createElement('div');
  head.className = 'group';
  head.textContent = reading === 'historical'
    ? S.legend_title_historical : S.legend_title_modern;
  wrap.appendChild(head);

  for (const row of legendRows(reading, overlay, has)) {
    const line = document.createElement('div');
    line.className = 'row';
    const key = document.createElement('span');
    key.className = 'key';
    const canvas = document.createElement('canvas');
    canvas.width = t.fig.swatch_w;
    canvas.height = t.fig.swatch_h;
    canvas.style.verticalAlign = 'middle';
    canvas.dataset.row = row.key;
    key.appendChild(canvas);
    const does = document.createElement('span');
    does.className = 'does';
    does.textContent = row.label;
    line.append(key, does);
    wrap.appendChild(line);
    paintSwatch(canvas, row, r.alpha, stepPx(faceOf(reading), t.text.letter), reading);
  }
  return wrap;
}

/**
 * Paints one swatch on its own canvas.
 *
 * jsdom hands back no 2D context and a browser may refuse one, so a swatch
 * that cannot be painted is left blank and the words still read.
 */
function paintSwatch(
  canvas: HTMLCanvasElement, row: LegendRow, alpha: number,
  sizePx: number, reading: ChartReading,
): void {
  const c2d = canvas.getContext('2d');
  if (!c2d) return;
  const x = canvas.width / 2, y = canvas.height / 2;
  // `Ctx2D` is the narrow half of the 2D context the adapter uses. The DOM
  // type widens `strokeStyle` to a gradient, which the adapter never sets.
  const sink = new CanvasSink(c2d as unknown as Ctx2D);
  sink.globalAlpha = alpha;
  row.paint(sink, x, y);
  if (!row.text) return;
  c2d.save();
  c2d.font = chartFont(faceOf(reading), sizePx);
  c2d.textAlign = 'center';
  c2d.textBaseline = 'middle';
  c2d.fillStyle = hex(row.text.color);
  c2d.globalAlpha = row.text.alpha * alpha;
  c2d.fillText(row.text.s, x, y);
  c2d.restore();
}
